import React, { useState, useEffect } from 'react';
import '../style/main.css';
import Home from './Home';
import Music from './Music';
import Scan from './Scan';
import Rewards from './Rewards';
import Profile from './Profile';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  fetchTrendingSongs,
  fetchArtists,
  fetchLanguages,
  fetchPlaylists,
} from '../utils/musicApi';
import BuyCoins from './CoinsModal';
import { AuthService, fetchWithAuth } from './authService';
import { API_ENDPOINTS } from '../utils/constants';
import { useStatsigClient, useStatsigUser } from '@statsig/react-bindings';

// NEW: import the new modal
import NewUserModal from './NewUserModal';
import ChancePage from './Chance';

const Main = () => {
  const [active, setActive] = useState(1);
  const [searchParams] = useSearchParams();
  const [djDetails, setdjdetails] = useState(null);
  const [city, setCity] = useState(null);
  const [name, setName] = useState('');
  const [userRegistered, setUserRegistered] = useState(true);
  const [profile, setProfile] = useState(null);
  const [trendings, setTrendings] = useState([]);
  const [artists, setArtists] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [playlists, setPlaylists] = useState([]);

  const [locationUpdateCount, setLocationUpdateCount] = useState(0);
  const { client } = useStatsigClient();
  const navigate = useNavigate();
  const { updateUserAsync } = useStatsigUser();

  const tabNames = {
    1: 'Home',
    2: 'Music',
    5: 'Profile',
    6: 'Wallet',
    7: 'Chance',
  };

  const handleActive = index => {
    localStorage.setItem('mainInd', Number(index));
    setActive(index);
    if (client) {
      client.logEvent('footerSelection', 'clicked', {
        source: localStorage.getItem('isQrEntry') == 'true' ? 'qrScan' : 'null',
        tabName: tabNames[active],
      });
    }
  };

  const clickOnVenue = vId => {
    navigate(`/venues/${vId}`);
  };

  // Redirect and restore active tab
  useEffect(() => {
    const token = localStorage.getItem('tringAuthToken');
    if (!token) {
      navigate('/');
      return;
    }
    const activemain = localStorage.getItem('mainInd');
    if (!activemain) {
      localStorage.setItem('mainInd', 1);
      setActive(1);
    } else {
      setActive(Number(activemain));
    }
  }, [navigate]);

  // Fetch venue by id
  useEffect(() => {
    const fetchEvent = async () => {
      const token = localStorage.getItem('tringAuthToken');
      if (!token || AuthService.isTokenExpired()) {
        setdjdetails(null);
        return;
      }
      const vId = localStorage.getItem('vId');
      if (!vId) {
        setdjdetails(null);
        localStorage.removeItem('venueName');
        return;
      }
      try {
        const djresponse = await fetchWithAuth(API_ENDPOINTS.VENUE_BY_ID(vId), {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!djresponse.ok) {
          localStorage.removeItem('vId');
          setdjdetails(null);
          throw new Error('Failed to fetch data');
        }
        let data;
        try {
          data = await djresponse.json();
        } catch {
          setdjdetails(null);
          return;
        }
        if (!data || (data.status !== 'active' && !data.isKJVenue)) {
          localStorage.removeItem('vId');
          localStorage.removeItem('venueName');
          setdjdetails(null);
          return;
        }
        setdjdetails(data);
      } catch (error) {
        console.error('Error fetching Venue:', error);
        setdjdetails(null);
      }
    };
    fetchEvent();
  }, [searchParams]);

  // Fetch profile and detect default name
  useEffect(() => {
    const token = localStorage.getItem('tringAuthToken');
    if (!token || AuthService.isTokenExpired()) {
      navigate('/');
      return;
    }
    const fetchProfile = async () => {
      try {
        const response = await fetchWithAuth(API_ENDPOINTS.USERS, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) throw new Error('Failed to fetch data');
        const data = await response.json();
        setProfile(data);
        if (data?.isDefaultName) {
          setUserRegistered(false);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };
    fetchProfile();
  }, [navigate]);

  // Initial data load
  useEffect(() => {
    const loadInitialData = async () => {
      const token = localStorage.getItem('tringAuthToken');
      if (!token) return;
      try {
        if (localStorage.getItem('trendingsongs')) {
          setTrendings(JSON.parse(localStorage.getItem('trendingsongs')));
        } else {
          const songs = await fetchTrendingSongs(token);
          setTrendings(songs);
        }
        if (localStorage.getItem('artists')) {
          setArtists(JSON.parse(localStorage.getItem('artists')));
        } else {
          const artists = await fetchArtists(token);
          setArtists(artists);
        }
        if (localStorage.getItem('languages')) {
          setLanguages(JSON.parse(localStorage.getItem('languages')));
        } else {
          const languages = await fetchLanguages(token);
          setLanguages(languages);
        }
        if (localStorage.getItem('playlists')) {
          setPlaylists(JSON.parse(localStorage.getItem('playlists')));
        } else {
          const playlists = await fetchPlaylists(token);
          setPlaylists(playlists);
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };
    loadInitialData();
  }, []);

  // Geolocation -> city store
  useEffect(() => {
    const token = localStorage.getItem('tringAuthToken');
    if (!token) return;
    const requestLocationPermission = async () => {
      if (!navigator.geolocation) {
        console.log('Geolocation not supported');
        return;
      }
      try {
        const permission = await navigator.permissions.query({
          name: 'geolocation',
        });
        if (permission.state === 'granted') {
          getLocation();
        } else if (permission.state === 'prompt') {
          navigator.geolocation.getCurrentPosition(
            () => getLocation(),
            error => console.error('Location permission denied', error)
          );
        }
      } catch (error) {
        console.error('Error checking location permission:', error);
      }
    };
    const getLocation = async () => {
      navigator.geolocation.getCurrentPosition(
        async position => {
          const { latitude, longitude } = position.coords;
          try {
            const response = await fetchWithAuth(
              `${API_ENDPOINTS.VENUES_CITY}?isPopular=false&lat=${latitude}&lon=${longitude}`,
              {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
              }
            );
            if (response.ok) {
              const data = await response.json();
              const cityName = data?.currentCity?.city;
              if (cityName) {
                localStorage.setItem('selectedCity', cityName);
                localStorage.setItem('latitude', latitude);
                localStorage.setItem('longitude', longitude);
                setLocationUpdateCount(prev => prev + 1);
              }
            }
          } catch (error) {
            console.error('Error fetching city name:', error);
          }
        },
        error => console.error('Error getting location:', error),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    };
    if (!localStorage.getItem('selectedCity')) requestLocationPermission();
  }, []);

  return (
    <div
      className="main-container d-flex flex-column"
      style={{ position: 'relative', height: '100dvh' }}
    >
      {/* New modal, rendered only if user is not registered */}
      {!userRegistered && (
        <NewUserModal
          defaultName={name}
          onClose={() => setUserRegistered(true)}
          onRegistered={finalName => {
            setName(finalName || '');
            setUserRegistered(true);
          }}
          updateUserAsync={updateUserAsync}
        />
      )}

      <div
        className="w-100"
        style={{ flex: 1, overflowY: 'auto', paddingBottom: '9dvh' }}
      >
        {active === 1 ? (
          <Home
            setActive={setActive}
            trendings={trendings}
            artists={artists}
            playlists={playlists}
            locationUpdateCount={locationUpdateCount}
          />
        ) : null}
        {active === 2 ? (
          <Music
            trendings={trendings}
            artists={artists}
            languages={languages}
            playlists={playlists}
          />
        ) : null}
        {active === 3 ? <Scan /> : null}
        {active === 4 ? <Rewards /> : null}
        {active === 5 ? <Profile /> : null}
        {active === 6 ? <BuyCoins /> : null}
        {active === 7 ? <ChancePage setActive={setActive} /> : null}
      </div>

      <div
        className="navbar-container d-flex flex-column align-items-center pb-2"
        style={{ position: 'fixed', bottom: 0 }}
      >
        {djDetails &&
          (djDetails.status === 'active' ||
            (djDetails.isKJVenue && djDetails.status === 'approved')) &&
          active !== 2 &&
          active !== 6 &&
          active !== 7 && (
            <button
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                background: 'linear-gradient(90deg, #3E286D 0%, #873662 100%)',
                cursor: 'pointer',
                border: 'none',
                outline: 'none',
                position: 'fixed', // ensure it's fixed above footer
                bottom: '9dvh', // sits above footer
              }}
              onClick={() => clickOnVenue(djDetails?.id)}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  width: '66.66%',
                  gap: '1dvh',
                  padding: '0.7rem 0.3rem',
                }}
              >
                <img
                  src={djDetails?.owner?.avatar || djDetails?.photoUrl}
                  style={{
                    borderRadius: '50%',
                    width: 'clamp(0px,9dvw,35px)',
                    aspectRatio: '1/1',
                  }}
                  alt="DJ or Venue"
                />
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    width: '91.66%',
                    marginLeft: '5px',
                    gap: '0.2rem',
                  }}
                >
                  <span
                    style={{
                      fontSize: 'clamp(0px,4dvw,16px)',
                      color: 'white',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      width: '100%',
                      textAlign: 'left',
                      lineHeight: '1.1',
                    }}
                  >
                    {djDetails?.name}
                  </span>
                  <span
                    style={{
                      fontSize: 'clamp(0px,3.4dvw,14px)',
                      color: '#868686',
                      lineHeight: '1.1',
                    }}
                  >
                    {djDetails?.owner?.display_name}
                  </span>
                </div>
              </div>

              <span
                style={{
                  padding: '0.25rem 0.5rem',
                  backgroundColor:
                    djDetails.status !== 'active' ? '#D0151C' : '#0F6D01',
                  color: 'white',
                  borderRadius: '7px',
                  fontSize: '2dvh',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {djDetails.status === 'active' && (
                  <img
                    src="/images/redDot2.gif"
                    style={{
                      width: 'clamp(0px,4vw,16px)',
                      aspectRatio: '1/1',
                      background: 'transparent',
                      marginRight: '5px',
                      fontSize: '16px',
                    }}
                    alt="Live"
                  />
                )}
                {djDetails.status !== 'active' ? 'Upcoming' : 'Live'}
              </span>
            </button>
          )}

        {active !== 2 && (
          <div
            style={{
              height: '9.3dvh',
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1000,
              backgroundColor: 'white',
            }}
            className="d-flex flex-row justify-content-between align-items-center w-100 px-2 py-1"
          >
            <button
              style={{
                border: 'none',
                backgroundColor: 'white',
                height: '7dvh',
              }}
              className="d-flex flex-column align-items-center"
              onClick={() => handleActive(1)}
            >
              <img
                src={active === 1 ? '/images/nav1r.svg' : '/images/nav1.svg'}
                alt="logo"
                className="mb-1"
              />
              <span
                style={{ color: active === 1 ? '#DB0F11' : '#4c4c4c' }}
                className="common-txt"
              >
                Home
              </span>
            </button>

            <button
              style={{
                border: 'none',
                backgroundColor: 'white',
                height: '7dvh',
              }}
              className="d-flex flex-column align-items-center"
              onClick={() => handleActive(7)}
            >
              <img
                src={'/images/coin.svg'}
                alt="logo"
                className={`mb-1 ${active === 7 ? '' : 'coin-spin'}`}
                style={{ height: '25px', width: '25px' }}
              />
              <span
                style={{ color: active === 7 ? '#DB0F11' : '#4c4c4c' }}
                className="common-txt"
              >
                Chance
              </span>
            </button>

            <button
              style={{
                border: 'none',
                backgroundColor: 'white',
                height: '7dvh',
              }}
              className="d-flex flex-column align-items-center"
              onClick={() => handleActive(6)}
            >
              <img
                src={active === 6 ? '/images/nav4r.svg' : '/images/nav4.svg'}
                alt="logo"
                className="mb-1"
              />
              <span
                style={{ color: active === 6 ? '#DB0F11' : '#4c4c4c' }}
                className="common-txt"
              >
                Wallet
              </span>
            </button>

            <button
              onClick={() => handleActive(5)}
              style={{
                border: 'none',
                backgroundColor: 'white',
                height: '7dvh',
              }}
              className="d-flex flex-column align-items-center"
            >
              <img
                src={profile?.avatar || '/images/default-avatar.png'}
                alt="profile avatar"
                className="mb-1"
                style={{
                  width: '25px',
                  height: '25px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border:
                    active === 5
                      ? '2px solid #DB0F11'
                      : '2px solid transparent',
                }}
              />
              <span
                style={{ color: active === 5 ? '#DB0F11' : '#4c4c4c' }}
                className="common-txt"
              >
                {profile?.first_name}
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Main;
